import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPin, Phone, MessageCircle, Clock, CheckCircle2, ChefHat,
  Car, Home, Package, Navigation, RefreshCw, ArrowLeft, Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import axios from 'axios';
import OrderChat from '@/components/chat/OrderChat';
import { useAuthStore } from '@/store';

import { API } from '@/config/api';

// Status icon mapping
const statusIcons = {
  receipt: CheckCircle2,
  check: CheckCircle2,
  chef: ChefHat,
  package: Package,
  car: Car,
  box: Package,
  navigation: Navigation,
  home: Home,
  store: MapPin,
  'check-circle': CheckCircle2
};

// Timeline Step Component
const TimelineStep = ({ step, isLast }) => {
  const Icon = statusIcons[step.icon] || CheckCircle2;
  
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center",
          step.completed ? "bg-primary text-white" : 
          step.active ? "bg-primary/20 text-primary border-2 border-primary" :
          "bg-muted text-muted-foreground"
        )}>
          <Icon className="h-5 w-5" />
        </div>
        {!isLast && (
          <div className={cn(
            "w-0.5 h-12 my-1",
            step.completed ? "bg-primary" : "bg-muted"
          )} />
        )}
      </div>
      <div className="flex-1 pb-4">
        <p className={cn(
          "font-medium",
          step.completed || step.active ? "text-foreground" : "text-muted-foreground"
        )}>
          {step.status}
        </p>
        <p className="text-sm text-muted-foreground">{step.description}</p>
        {step.time && (
          <p className="text-xs text-muted-foreground mt-1">
            {new Date(step.time).toLocaleTimeString()}
          </p>
        )}
      </div>
    </div>
  );
};

// Delivery progress as text: there is no map integration, so no positions are drawn.
const DeliveryMap = ({ restaurant, driverLocation }) => {
  const updated = driverLocation?.updated_at || driverLocation?.timestamp;
  return (
    <div className="space-y-2 text-sm" data-testid="delivery-progress-text">
      <div className="flex items-center gap-2">
        <MapPin className="h-4 w-4 text-red-500" />
        <span>From {restaurant?.name || 'the restaurant'}</span>
      </div>
      <div className="flex items-center gap-2">
        <Car className="h-4 w-4 text-primary" />
        <span>{driverLocation ? 'Your driver is on the way' : 'Waiting for a driver to pick up your order'}</span>
      </div>
      {updated && (
        <p className="text-xs text-muted-foreground">
          Driver location updated {new Date(updated).toLocaleTimeString()}
        </p>
      )}
    </div>
  );
};

export default function LiveOrderTracking() {
  const { id: orderId } = useParams();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChat, setShowChat] = useState(false);

  const fetchTracking = useCallback(async () => {
    try {
      // the customer's own session: tracking answers only a party to the order
      const token = useAuthStore.getState().token;
      const response = await axios.get(`${API}/delivery-tracking/order/${orderId}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
      setTracking(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load tracking information');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchTracking();
    // Set up polling for updates
    const interval = setInterval(fetchTracking, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, [fetchTracking]);

  // Calculate progress percentage
  const getProgressPercent = () => {
    if (!tracking?.timeline) return 0;
    const completed = tracking.timeline.filter(s => s.completed).length;
    return Math.round((completed / tracking.timeline.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <div className="text-center">
          <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
          <p className="text-muted-foreground">Loading tracking information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={fetchTracking}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/orders">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="font-semibold">Order #{orderId}</h1>
              <p className="text-sm text-muted-foreground">
                {tracking?.order_type === 'delivery' ? 'Delivery' : 'Pickup'}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchTracking}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        {/* ETA Card */}
        <Card className="bg-primary text-primary-foreground">
          <CardContent className="py-6 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Clock className="h-5 w-5" />
              <span className="text-lg font-medium">Estimated Arrival</span>
            </div>
            <p className="text-4xl font-bold mb-2">
              {tracking?.eta?.minutes || '--'} min
            </p>
            <p className="text-primary-foreground/80">
              {tracking?.eta?.label}
            </p>
            <Progress 
              value={getProgressPercent()} 
              className="mt-4 bg-primary-foreground/20 h-2"
            />
          </CardContent>
        </Card>

        {/* Map Section */}
        {tracking?.order_type === 'delivery' && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Navigation className="h-5 w-5 text-primary" />
                Delivery Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DeliveryMap 
                restaurant={tracking?.restaurant}
                driverLocation={tracking?.driver_location}
                customerLocation={tracking?.customer_location}
              />
            </CardContent>
          </Card>
        )}

        {/* Driver Info */}
        {tracking?.driver && (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={tracking.driver.photo_url} />
                    <AvatarFallback>
                      {tracking.driver.name?.charAt(0) || 'D'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{tracking.driver.name}</p>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      <span>{tracking.driver.rating}</span>
                      <span className="mx-1">•</span>
                      <span>{tracking.driver.vehicle_type}</span>
                    </div>
                  </div>
                </div>
                {tracking.can_contact_driver && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon">
                      <Phone className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon">
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Order Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-0">
              {tracking?.timeline?.map((step, index) => (
                <TimelineStep 
                  key={index} 
                  step={step} 
                  isLast={index === tracking.timeline.length - 1}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Restaurant Info */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">{tracking?.restaurant?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {tracking?.restaurant?.address}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.open(`tel:${tracking?.restaurant?.phone}`)}
              >
                <Phone className="h-4 w-4 mr-2" />
                Call
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Address */}
        {tracking?.order_type === 'delivery' && tracking?.delivery_address && (
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <Home className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Delivery Address</p>
                  <p className="text-sm text-muted-foreground">
                    {tracking.delivery_address}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {tracking?.can_cancel && (
          <Card className="border-destructive/50">
            <CardContent className="py-4">
              <Button variant="destructive" className="w-full">
                Cancel Order
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Contact Support Card */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Need Help?</p>
                <p className="text-sm text-muted-foreground">
                  Chat with the restaurant or driver
                </p>
              </div>
              <Button onClick={() => setShowChat(true)} className="gap-2">
                <MessageCircle className="h-4 w-4" />
                Open Chat
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Chat Component */}
      {showChat && (
        <OrderChat 
          orderId={parseInt(orderId)} 
          onClose={() => setShowChat(false)}
        />
      )}

      {/* Floating Chat Button (when chat is closed) */}
      {!showChat && (
        <button
          onClick={() => setShowChat(true)}
          className="fixed bottom-6 right-6 z-40 bg-primary text-white rounded-full p-4 shadow-lg hover:bg-primary/90 transition-all"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
